// Copyright: IQGeo Limited 2010-2019
import $ from 'jquery';
import _ from 'underscore';
import Promise from 'bluebird';
import myw from 'myWorld/base/core';
import i18n from 'i18next';

/** Localisation behaviour
 * @name LocalisationMixin
 * @mixin
 */
myw.LocalisationMixin =
    /** @lends LocalisationMixin */
    {
        /**
         * Returns a localized message.
         * Self's class will be used as the message group.
         * @param  {string}     messageId       Message key
         * @param  {string}     [positional]    Values for positional parameters in message
         * @return {string}     Translated message
         */
        msg(messageId, positional) {
            return myw.msg(this, messageId, positional);
        },

        /**
         * Walks over DOM tree and translates messages.
         * Self's class will be used as the message group
         * @param  {jQueryElement}    selector    jQuery selector for DOM tree.
         */
        translate(selector) {
            return myw.translate(this, selector);
        }
    };
myw.MywClass.include(myw.LocalisationMixin);

//****  add static localisation methods to myw ****
/**
 * Translate a message
 * @param  {string} messageGroup Name of message group
 * @param  {string} messageId    ID of message
 * @return {string} Translated message
 */
myw.msg = (messageGroup, messageId, positional) =>
    myw.localisation.msg(messageGroup, messageId, positional);

/**
 * Walks over DOM tree and translates messages
 * @param  {jQueryElement} selector jQuery selector for DOM tree.
 */
myw.translate = (groupOrObject, selector) => myw.localisation.translate(groupOrObject, selector);

myw.translateElement = (groupOrObject, element) =>
    myw.localisation.translateElement(groupOrObject, element);

/**
 * Set MessageGroup for each of the classes registered in the namespace. See myw.Localisation class for details.
 */
myw.setMessageGroups = () => {
    _.chain(myw)
        .keys()
        .filter(n => n[0].toUpperCase() == n[0])
        .each(n => {
            const proto = myw[n].prototype;
            if (proto && !proto.hasOwnProperty('messageGroup')) {
                proto.messageGroup = n;
            }
        });
};

myw.Localisation = myw.MywClass.extend('Localisation', {
    /**
     * @class  Encapsulates localisation facilities for myWorld client
     * @constructs
     */
    initialize() {
        this.i18n = i18n;

        this.language = this._getUrlParam('lang') || navigator.language;

        this._chacheBust = myw.buildVersion ? '?' + myw.buildVersion : '';

        //ENH: add a "installed languages" configuration so that we could prevent
        // missing files from being requested (ex: simplify 'en-GB' to 'en')

        this._initialized = new Promise(resolve => {
            this._initializedResolve = resolve;
        });

        //setup warning if init() is not called
        const promise = new Promise(resolve => (this._initCalled = resolve));
        promise.timeout(5000).catch(() => {
            console.log(
                'Warning: Localisation has not been initialized. Call myw.localisation.init()'
            );
        });

        /** Will resolve when initialized and all requested namespaces have been loaded
         * @type {promise} */
        this.ready = new Promise(resolve => (this._readyResolve = resolve));

        //  Keeps track of all of the promises generated by this class, used in conjunction with this.ready
        this._readyQueue = [];
    },

    /**
     * Adds a promise to a queue and resolves the ready promise when they are all fulfilled
     * @param {Promise} promise The promise to add to the tracking queue
     */
    _addToReadyQueue(promise) {
        this._readyQueue.push(promise);
        //  Once the promise is resolved, remove it from the list. Once all promises in the list are resolved, resolve the ready promise
        promise.then(() => {
            this._readyQueue = _.without(this._readyQueue, promise);
            if (this._readyQueue.length == 0) {
                this._readyResolve();
            }
        });
    },

    /**
     * Initialise localisation
     * @param  {Array<string>}  namespaces
     * @param  {string} [localeDirectory='locales']   Locale directory. Defaults to 'locales'
     * @return {Promise}
     * @private
     */
    init(namespaces = [], options = {}) {
        this._initCalled();
        //include system.settings file as we'll need it to set font-family
        namespaces = ['myw.system.settings'].concat(namespaces);
        const { languages = [] } = options;
        const langFamily = this.language ? this.language.split('-')[0] : this.language;
        const isExpectedLang = languages.includes(this.language) || languages.includes(langFamily);
        this.languageAsPerSetting =
            isExpectedLang && (languages.includes(this.language) ? this.language : langFamily);
        //if language is unexpected, use default language as fallback otherwise use default language (first in list) as fallback
        const fallbackLng = isExpectedLang ? false : languages[0];

        const { localeDirectory = (myw.baseUrl || '') + 'locales' } = options;
        const newPromise = new Promise(resolve => {
            i18n.init(
                {
                    resGetPath: `${localeDirectory}/__lng__/__ns__.msg${this._chacheBust}`,
                    ns: { namespaces, defaultNs: namespaces[0] },
                    lng: this.language,
                    fallbackLng,
                    debug: false,
                    returnObjectTrees: true,
                    getAsync: true, // Do not set to false;  leads loading timeout problems with libraries
                    fallbackNS: namespaces,
                    customLoad: this._customLoader.bind(this),
                    ...options
                },
                resolve
            );
        }).then(() => {
            // Set the font family for the application if its is provided message file
            // (this over-rides the font-family set in CSS)
            $('body').css('font-family', myw.msg('Font', 'font-family'));

            this._initializedResolve();
        });
        this._addToReadyQueue(newPromise);
        return newPromise;
    },

    /**
     * Load name spaces from specified locale directory
     * @param  {Array<string>} namespaces           List of Namespace names. For example ['myw.redline']
     * @param  {string} [localeDirectory]   Locale directory. Defaults to 'locales'
     * @return {Promise}
     */
    loadNamespaces(namespaces, localeDirectory) {
        localeDirectory = localeDirectory || 'locales';

        const promise = new Promise((resolve, reject) => {
            const newPath = localeDirectory + '/__lng__/__ns__.msg' + this._chacheBust;

            //ensure we've initialized before proceeding
            this.initialized.then(() => {
                this.i18n.options.resGetPath = newPath;
                this.i18n.loadNamespaces(namespaces, resolve);
            }, reject);
        });

        this._addToReadyQueue(promise);
        return promise;
    },

    /**
     * Load name space from specified locale directory
     * @param  {string} namespace           Namespace name. For example 'myw.redline'
     * @param  {string} [localeDirectory]   Locale directory. Defaults to 'locales'
     * @return {Promise}
     */
    loadNamespace(namespace, localeDirectory) {
        return this.loadNamespaces([namespace], localeDirectory);
    },

    /**
     * Load name space for a module
     * @param  {string} moduleName Name of module
     * @param  {'client'|'config'} [application] Identifies if should load client or configuration file. Defaults to 'client'
     * @return {Promise}
     */
    loadModuleLocale(moduleName, application = 'client') {
        const appSuff = application == 'client' ? '' : '.' + application;
        return this.loadNamespace(moduleName + appSuff, `modules/${moduleName}/locales`);
    },

    /**
     * Custom loader that allows us to define static language resources and to have
     * these used rather than loading from the network.
     * @private
     */
    _customLoader(lng, ns, options, loadComplete) {
        //ENH: this method is not necessary anymore?
        i18n.sync._fetchOne(lng, ns, options, loadComplete);
    },

    /**
     * Walks over DOM tree and translates messages
     * @param  {string|object} groupOrObject Either name of message group or object to look for messageGroup on. If
     * the latter and the message is not found, then the message be looked for on the parent class of the object.
     * @param  {jQuerySelector} selector jQuery selector for DOM tree.
     * @return {promise}  Resolves when translation has finished
     */
    translate(groupOrObject, selector) {
        return this.ready.then(() => {
            myw.trace('i18n', 3, 'Start DOM translate ' + selector.selector);
            this._translate(groupOrObject, selector);
            myw.trace('i18n', 3, 'End DOM translate');
        });
    },

    /**
     * Translate the inner HTML of an element
     * @param  {string|object} groupOrObject Either name of message group or object to look for messageGroup on. If
     * the latter and the message is not found, then the message be looked for on the parent class of the object.
     * @param  {element} Element
     * @return {promise}  Resolves when translation has finished
     */
    translateElement(groupOrObject, element) {
        return this.ready.then(() => {
            myw.trace('i18n', 3, 'Start element translate ');
            element.text = this.translateHtml(groupOrObject, element.text);
            myw.trace('i18n', 3, 'End element translate');
        });
    },

    /**
     * Translate HTML string
     * @param  {string|object} groupOrObject Either name of message group or object to look for messageGroup on. If
     * the latter and the message is not found, then the message be looked for on the parent class of the object.
     * @param {string} string to translate
     */
    translateHtml(groupOrObject, string) {
        const list = string.match(/{:(.*?)}/g);
        let i;
        let key;
        let value;

        if (!list) return string;

        for (i = 0; i < list.length; i++) {
            key = list[i].substring(2, list[i].length - 1);
            value = this.msg(groupOrObject, key);
            string = string.replace(list[i], value);
        }
        return string;
    },

    _translate(groupOrObject, selector) {
        let n, i, k;

        // Find text nodes with tag prefix and translate them)
        let nodes = selector
            .find('*')
            .contents()
            .filter(function() {
                //'this' will be jquery element - don't convert to arrow function
                return (
                    this.nodeType == 3 && this.data && this.data[0] && this.data[0].indexOf('{:')
                );
            });

        for (i = 0; i < nodes.length; i++) {
            n = nodes[i];
            n.data = this.translateHtml(groupOrObject, n.data);
        }

        // Find these attributes and translate them
        // ENH ID is needed for print layouts. Maybe make it optional
        const attrNames = ['title', 'alt', 'id', 'label'];

        for (k = 0; k < attrNames.length; k++) {
            const attrName = attrNames[k];
            nodes = selector.find('[' + attrName + "*='{:']");

            for (i = 0; i < nodes.length; i++) {
                n = $(nodes[i]);
                const attrValue = n.attr(attrName);

                // ENH Some cases the attr value is undefined even though it was picked up
                // in the find
                if (attrValue) n.attr(attrName, this.translateHtml(groupOrObject, attrValue));
            }
        }
    },

    /**
     * Translation function.
     * @param  {string|object} groupOrObject Either name of message group or object to look for messageGroup on. If
     * the latter and the message is not found, then the message be looked for on the parent class of the object.
     * @param  {string} messageId Message key
     * @param  {string} positional Values for positional parameters in message string
     * @return {string} Translated message
     */
    msg(groupOrObject, messageId, positional) {
        let msg;

        if (typeof groupOrObject == 'string') {
            // Replace / with . This sorts of groupOrObject strings can arising from config application where we
            // use the 'type' value which can sometimes be hierarchical (such as replicas/extract)
            groupOrObject = groupOrObject.replace('/', '_');

            msg = this._msg(groupOrObject, messageId, positional);

            // Go up message inheritance tree looking for message
            while (msg === undefined || (msg.indexOf && msg.indexOf('plural_not_found') === 0)) {
                const newGroupOrObject = this._msg(groupOrObject, 'myw_inherit_from');
                if (!newGroupOrObject) break;
                groupOrObject = newGroupOrObject;
                msg = this._msg(groupOrObject, messageId, positional);
            }
            if (msg === undefined) msg = groupOrObject + '.' + messageId;
        } else {
            let proto = groupOrObject.constructor.prototype;
            const firstGrp = proto.messageGroup;

            // Go up the class inheritance tree looking for a message
            while (proto && proto.messageGroup) {
                msg = this._msg(proto.messageGroup, messageId, positional);
                if (msg !== undefined && (!msg.indexOf || msg.indexOf('plural_not_found') !== 0))
                    return msg;
                proto = proto.constructor.__super__;
            }
            if (msg === undefined) {
                if (firstGrp) msg = firstGrp + '.' + messageId;
                else msg = messageId;
            }
        }

        return msg;
    },

    // Looks up message
    _msg(prefix, messageId, positional) {
        let key,
            s,
            kprefix = '';

        if (prefix) {
            kprefix = prefix + '.';
        }

        const options = {};
        if (positional) {
            _.extend(options, positional);
        }

        key = kprefix + messageId;

        s = this.i18n.t(key, options);
        if (s === key) return undefined;
        return s;
    },

    _getUrlParam(name) {
        name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');

        const regexS = '[\\?&]' + name + '=([^&#]*)';
        const regex = new RegExp(regexS);
        const results = regex.exec(window.location.href);

        if (results === null) {
            return '';
        } else {
            return results[1];
        }
    },

    // Utility for finding all text nodes in the DOM that are not composed entirely of whitespace.
    findAll() {
        let i;
        let n;
        const result = document.evaluate(
            './/text()',
            document,
            null,
            XPathResult.ORDERED_NODE_SNAPSHOT_TYPE,
            null
        );

        for (i = 0; i < result.snapshotLength; i++) {
            n = result.snapshotItem(i);
            if (n.data.length > 0 && !n.data.match(/\s+/)) {
                // Go up looking for ancestor with an id. Helps to locate text in application
                let parent = n.parentElement,
                    id = '';
                while (parent) {
                    if (parent.id) {
                        id = parent.id;
                        break;
                    }
                    parent = parent.parentElement;
                }
                console.log(id, n.data);
            }
        }
    }
});

//define getters and setters
Object.defineProperty(myw.Localisation.prototype, 'initialized', {
    // database version for all operations.
    get: function() {
        if (!this._initialized)
            throw new Error('Localisation has not been initialized. Call myw.localisation.init()');
        return this._initialized;
    }
});

// Return an instance of this class
myw.localisation = new myw.Localisation();
export default myw.localisation;
